import { Viewer } from "@photo-sphere-viewer/core";
import { GyroscopePlugin } from "@photo-sphere-viewer/gyroscope-plugin";
import { StereoPlugin } from "@photo-sphere-viewer/stereo-plugin";
import exifr from "exifr";
import "@photo-sphere-viewer/core/index.css";
import "./style.css";

type Photo = {
  id: string;
  name: string;
  size: number;
  uploadedAt: string;
  url: string;
  gps: Gps | null;
};

type Gps = { latitude: number; longitude: number };

type PhotoListResponse = {
  photos: Photo[];
  maxUploadBytes: number;
};

type UploadState = "idle" | "checking" | "uploading" | "saving" | "refreshing" | "success" | "error";

const elements = {
  viewer: document.querySelector<HTMLElement>("#viewer")!,
  gallery: document.querySelector<HTMLElement>("#gallery")!,
  emptyGallery: document.querySelector<HTMLElement>("#empty-gallery")!,
  emptyViewer: document.querySelector<HTMLElement>("#empty-viewer")!,
  fileInput: document.querySelector<HTMLInputElement>("#file-input")!,
  uploadButton: document.querySelector<HTMLButtonElement>("#upload-button")!,
  downloadButton: document.querySelector<HTMLButtonElement>("#download-button")!,
  renameButton: document.querySelector<HTMLButtonElement>("#rename-button")!,
  deleteButton: document.querySelector<HTMLButtonElement>("#delete-button")!,
  keyInput: document.querySelector<HTMLInputElement>("#key-input")!,
  saveKeyButton: document.querySelector<HTMLButtonElement>("#save-key")!,
  keyStatus: document.querySelector<HTMLElement>("#key-status")!,
  dropZone: document.querySelector<HTMLElement>("#drop-zone")!,
  toast: document.querySelector<HTMLElement>("#toast")!,
  currentName: document.querySelector<HTMLElement>("#current-name")!,
  currentMeta: document.querySelector<HTMLElement>("#current-meta")!,
  currentLocation: document.querySelector<HTMLAnchorElement>("#current-location")!,
  renameDialog: document.querySelector<HTMLDialogElement>("#rename-dialog")!,
  renameForm: document.querySelector<HTMLFormElement>("#rename-form")!,
  renameInput: document.querySelector<HTMLInputElement>("#rename-input")!,
  renameCancel: document.querySelector<HTMLButtonElement>("#rename-cancel")!,
  renameSubmit: document.querySelector<HTMLButtonElement>("#rename-submit")!,
  uploadStatus: document.querySelector<HTMLElement>("#upload-status")!,
  uploadStatusTitle: document.querySelector<HTMLElement>("#upload-status-title")!,
  uploadStatusDetail: document.querySelector<HTMLElement>("#upload-status-detail")!,
  uploadPercent: document.querySelector<HTMLElement>("#upload-percent")!,
  progress: document.querySelector<HTMLProgressElement>("#upload-progress")!,
};

let photos: Photo[] = [];
let currentPhoto: Photo | null = null;
let viewer: Viewer | null = null;
let toastTimer: number | undefined;

function uploadKey(): string {
  return sessionStorage.getItem("sphere-upload-key") ?? "";
}

function setKeyStatus(): void {
  const hasKey = Boolean(uploadKey());
  elements.keyStatus.textContent = hasKey ? "密钥已在本次会话中启用" : "上传和删除需要密钥";
  elements.keyStatus.classList.toggle("is-active", hasKey);
  elements.keyInput.value = "";
  elements.keyInput.placeholder = hasKey ? "输入新密钥以替换" : "输入上传密钥";
}

function showToast(message: string, tone: "success" | "error" = "success"): void {
  window.clearTimeout(toastTimer);
  elements.toast.textContent = message;
  elements.toast.dataset.tone = tone;
  elements.toast.classList.add("is-visible");
  toastTimer = window.setTimeout(() => elements.toast.classList.remove("is-visible"), 3600);
}

function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function formatDate(value: string): string {
  return new Intl.DateTimeFormat("zh-CN", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

function formatGps(gps: Gps): string {
  const latitude = `${Math.abs(gps.latitude).toFixed(6)}°${gps.latitude >= 0 ? "N" : "S"}`;
  const longitude = `${Math.abs(gps.longitude).toFixed(6)}°${gps.longitude >= 0 ? "E" : "W"}`;
  return `${latitude}, ${longitude}`;
}

function mapUrl(gps: Gps): string {
  const latitude = gps.latitude.toFixed(6);
  const longitude = gps.longitude.toFixed(6);
  return `https://www.openstreetmap.org/?mlat=${latitude}&mlon=${longitude}#map=15/${latitude}/${longitude}`;
}

async function readGps(file: File): Promise<Gps | null> {
  try {
    const gps = await exifr.gps(file);
    if (!gps || !Number.isFinite(gps.latitude) || !Number.isFinite(gps.longitude)) return null;
    return { latitude: gps.latitude, longitude: gps.longitude };
  } catch {
    return null;
  }
}

async function apiError(response: Response): Promise<Error> {
  const fallback = `请求失败（${response.status}）`;
  try {
    const data = (await response.json()) as { error?: string };
    return new Error(data.error ?? fallback);
  } catch {
    return new Error(fallback);
  }
}

function setUploadStatus(state: UploadState, title: string, detail: string, percent: number): void {
  const value = Math.max(0, Math.min(100, Math.round(percent)));
  elements.uploadStatus.hidden = state === "idle";
  elements.uploadStatus.dataset.state = state;
  elements.uploadStatusTitle.textContent = title;
  elements.uploadStatusDetail.textContent = detail;
  elements.uploadPercent.textContent = state === "error" ? "失败" : `${value}%`;
  elements.progress.value = value;
}

function uploadRequest(file: File, key: string, gps: Gps | null): Promise<Photo> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open("POST", "/api/photos");
    request.responseType = "json";
    request.setRequestHeader("authorization", `Bearer ${key}`);
    request.setRequestHeader("content-type", file.type);
    request.setRequestHeader("x-photo-name", encodeURIComponent(file.name));
    if (gps) {
      request.setRequestHeader("x-photo-latitude", String(gps.latitude));
      request.setRequestHeader("x-photo-longitude", String(gps.longitude));
    }

    request.upload.addEventListener("progress", (event) => {
      if (!event.lengthComputable) {
        setUploadStatus("uploading", "正在上传", `${file.name} · ${formatBytes(file.size)}`, 25);
        return;
      }
      const percent = (event.loaded / event.total) * 92;
      setUploadStatus(
        "uploading",
        "正在上传到 Cloudflare",
        `${formatBytes(event.loaded)} / ${formatBytes(event.total)} · ${file.name}`,
        percent,
      );
    });

    request.upload.addEventListener("load", () => {
      setUploadStatus("saving", "正在保存到 R2", "文件已传输，等待 Cloudflare 完成写入…", 94);
    });

    request.addEventListener("load", () => {
      const payload = request.response as { photo?: Photo; error?: string } | null;
      if (request.status >= 200 && request.status < 300 && payload?.photo) {
        resolve(payload.photo);
        return;
      }

      const localHint = request.status === 404
        ? "找不到上传接口。请使用 npm run cf:dev 启动完整应用，而不是 npm run dev。"
        : undefined;
      reject(new Error(payload?.error ?? localHint ?? `上传失败（HTTP ${request.status}）。`));
    });
    request.addEventListener("error", () => {
      reject(new Error("无法连接上传服务。请检查网络，或确认 Cloudflare Worker 正在运行。"));
    });
    request.addEventListener("timeout", () => {
      reject(new Error("上传等待时间过长，请检查网络后重试。"));
    });
    request.timeout = 10 * 60 * 1000;
    request.send(file);
  });
}

function updateCurrentInfo(): void {
  elements.currentName.textContent = currentPhoto?.name ?? "尚未选择照片";
  elements.currentMeta.textContent = currentPhoto
    ? `${formatBytes(currentPhoto.size)} · ${formatDate(currentPhoto.uploadedAt)}`
    : "从照片库选择一张全景图";
  elements.deleteButton.disabled = !currentPhoto;
  elements.downloadButton.disabled = !currentPhoto;
  elements.renameButton.disabled = !currentPhoto;
  if (currentPhoto?.gps) {
    elements.currentLocation.hidden = false;
    elements.currentLocation.textContent = `⌖ ${formatGps(currentPhoto.gps)} · 查看地图`;
    elements.currentLocation.href = mapUrl(currentPhoto.gps);
  } else {
    elements.currentLocation.hidden = true;
    elements.currentLocation.removeAttribute("href");
    elements.currentLocation.textContent = "";
  }
}

async function selectPhoto(photo: Photo): Promise<void> {
  currentPhoto = photo;
  updateCurrentInfo();
  elements.emptyViewer.hidden = true;

  document.querySelectorAll(".photo-card").forEach((card) => {
    card.classList.toggle("is-selected", card.getAttribute("data-id") === photo.id);
  });

  try {
    if (!viewer) {
      viewer = new Viewer({
        container: elements.viewer,
        panorama: photo.url,
        caption: photo.name,
        navbar: ["zoom", "move", "gyroscope", "stereo", "caption", "fullscreen"],
        plugins: [
          GyroscopePlugin.withConfig({
            moveMode: "fast",
            touchmove: true,
            roll: true,
          }),
          StereoPlugin,
        ],
        lang: {
          gyroscope: "头部方向控制",
          stereo: "VR 360（2D）",
          stereoNotification: "当前为 2D 球面全景 · 点击任意位置退出 VR",
          pleaseRotate: "请旋转设备",
          tapToContinue: "或点击继续",
        },
        defaultZoomLvl: 42,
        mousewheelCtrlKey: false,
        touchmoveTwoFingers: false,
      });
    } else {
      await viewer.setPanorama(photo.url, { caption: photo.name, transition: { speed: 550 } });
    }
  } catch {
    showToast("无法打开图片，请确认它是等距柱状投影的全景图。", "error");
  }
}

function renderGallery(): void {
  elements.gallery.replaceChildren();
  elements.emptyGallery.hidden = photos.length > 0;

  for (const photo of photos) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "photo-card";
    button.dataset.id = photo.id;
    button.classList.toggle("is-selected", currentPhoto?.id === photo.id);
    button.innerHTML = `
      <span class="photo-card__preview"><img loading="lazy" alt="" src="${photo.url}"></span>
      <span class="photo-card__body">
        <strong></strong>
        <small>${formatBytes(photo.size)} · ${formatDate(photo.uploadedAt)}</small>
        ${photo.gps ? `<small class="photo-card__location">⌖ ${formatGps(photo.gps)}</small>` : ""}
      </span>
    `;
    button.querySelector("strong")!.textContent = photo.name;
    button.addEventListener("click", () => void selectPhoto(photo));
    elements.gallery.append(button);
  }
}

async function loadPhotos(selectNewest = false): Promise<void> {
  const response = await fetch("/api/photos");
  if (!response.ok) throw await apiError(response);
  const data = (await response.json()) as PhotoListResponse;
  photos = data.photos;
  renderGallery();

  if (selectNewest && photos[0]) await selectPhoto(photos[0]);
}

async function upload(file: File): Promise<void> {
  setUploadStatus("checking", "正在检查文件", `${file.name} · ${formatBytes(file.size)}`, 2);
  const key = uploadKey();
  if (!key) {
    elements.keyInput.focus();
    setUploadStatus("error", "缺少管理密钥", "请先在下方输入管理密钥并点击“启用”，然后重新上传。", 0);
    showToast("请先输入上传密钥。", "error");
    return;
  }
  if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
    setUploadStatus("error", "不支持此文件格式", "仅支持 JPG、PNG 或 WebP 图片。", 0);
    showToast("请选择 JPG、PNG 或 WebP 图片。", "error");
    return;
  }
  if (file.size > 50 * 1024 * 1024) {
    setUploadStatus("error", "文件太大", `${file.name} 为 ${formatBytes(file.size)}，当前限制为 50 MB。`, 0);
    showToast("图片不能超过 50 MB。", "error");
    return;
  }

  elements.uploadButton.disabled = true;
  elements.uploadButton.textContent = "上传中…";

  try {
    setUploadStatus("checking", "正在读取照片信息", "检查 EXIF GPS 信息…", 4);
    const gps = await readGps(file);
    setUploadStatus(
      "checking",
      gps ? "已读取 GPS 信息" : "未发现 GPS 信息",
      gps ? formatGps(gps) : "照片将不显示拍摄位置。",
      6,
    );
    await uploadRequest(file, key, gps);
    setUploadStatus("refreshing", "正在刷新照片库", "照片已保存，正在载入全景预览…", 98);
    await loadPhotos(true);
    setUploadStatus("success", "上传成功", `${file.name} 已保存到 Cloudflare R2。`, 100);
    showToast("全景照片已保存到 Cloudflare R2。", "success");
  } catch (error) {
    const message = error instanceof Error ? error.message : "上传失败，请重试。";
    setUploadStatus("error", "上传失败", message, 0);
    showToast(message, "error");
  } finally {
    elements.uploadButton.disabled = false;
    elements.uploadButton.textContent = "选择照片";
    elements.fileInput.value = "";
  }
}

function openRenameDialog(): void {
  if (!currentPhoto) return;
  if (!uploadKey()) {
    elements.keyInput.focus();
    showToast("重命名照片需要管理密钥。", "error");
    return;
  }
  elements.renameInput.value = currentPhoto.name;
  elements.renameDialog.showModal();
  elements.renameInput.focus();
  elements.renameInput.select();
}

function downloadCurrent(): void {
  if (!currentPhoto) return;
  const link = document.createElement("a");
  link.href = `${currentPhoto.url}?download=1`;
  document.body.append(link);
  link.click();
  link.remove();
  showToast("已开始下载原始照片。", "success");
}

async function renameCurrent(name: string): Promise<void> {
  if (!currentPhoto) return;
  const key = uploadKey();
  if (!key) return;

  const id = currentPhoto.id;
  elements.renameSubmit.disabled = true;
  elements.renameSubmit.textContent = "保存中…";
  try {
    const response = await fetch(`/api/photos/${encodeURIComponent(id)}`, {
      method: "PATCH",
      headers: {
        authorization: `Bearer ${key}`,
        "content-type": "application/json",
      },
      body: JSON.stringify({ name }),
    });
    if (!response.ok) throw await apiError(response);
    const data = (await response.json()) as { photo: Photo };
    photos = photos.map((photo) => (photo.id === id ? data.photo : photo));
    currentPhoto = data.photo;
    renderGallery();
    updateCurrentInfo();
    viewer?.setOptions({ caption: data.photo.name });
    elements.renameDialog.close();
    showToast("照片名称已更新。", "success");
  } catch (error) {
    showToast(error instanceof Error ? error.message : "重命名失败。", "error");
  } finally {
    elements.renameSubmit.disabled = false;
    elements.renameSubmit.textContent = "保存名称";
  }
}

async function deleteCurrent(): Promise<void> {
  if (!currentPhoto) return;
  const key = uploadKey();
  if (!key) {
    elements.keyInput.focus();
    showToast("删除照片需要上传密钥。", "error");
    return;
  }
  if (!window.confirm(`确定永久删除“${currentPhoto.name}”吗？`)) return;

  const photo = currentPhoto;
  elements.deleteButton.disabled = true;
  try {
    const response = await fetch(`/api/photos/${encodeURIComponent(photo.id)}`, {
      method: "DELETE",
      headers: { authorization: `Bearer ${key}` },
    });
    if (!response.ok) throw await apiError(response);
    currentPhoto = null;
    viewer?.destroy();
    viewer = null;
    elements.emptyViewer.hidden = false;
    updateCurrentInfo();
    await loadPhotos();
    showToast("照片已删除。", "success");
  } catch (error) {
    showToast(error instanceof Error ? error.message : "删除失败。", "error");
    elements.deleteButton.disabled = false;
  }
}

elements.saveKeyButton.addEventListener("click", () => {
  const value = elements.keyInput.value.trim();
  if (!value) {
    sessionStorage.removeItem("sphere-upload-key");
    showToast("本次会话的密钥已清除。", "success");
  } else {
    sessionStorage.setItem("sphere-upload-key", value);
    showToast("密钥已在本次浏览器会话中保存。", "success");
  }
  setKeyStatus();
});

elements.keyInput.addEventListener("keydown", (event) => {
  if (event.key === "Enter") elements.saveKeyButton.click();
});

elements.uploadButton.addEventListener("click", () => elements.fileInput.click());
elements.downloadButton.addEventListener("click", downloadCurrent);
elements.renameButton.addEventListener("click", openRenameDialog);
elements.renameCancel.addEventListener("click", () => elements.renameDialog.close());
elements.renameForm.addEventListener("submit", (event) => {
  event.preventDefault();
  const name = elements.renameInput.value.trim();
  if (name) void renameCurrent(name);
});
elements.fileInput.addEventListener("change", () => {
  const file = elements.fileInput.files?.[0];
  if (file) void upload(file);
});
elements.deleteButton.addEventListener("click", () => void deleteCurrent());

for (const eventName of ["dragenter", "dragover"]) {
  elements.dropZone.addEventListener(eventName, (event) => {
    event.preventDefault();
    elements.dropZone.classList.add("is-dragging");
  });
}
for (const eventName of ["dragleave", "drop"]) {
  elements.dropZone.addEventListener(eventName, (event) => {
    event.preventDefault();
    elements.dropZone.classList.remove("is-dragging");
  });
}
elements.dropZone.addEventListener("drop", (event) => {
  const file = event.dataTransfer?.files[0];
  if (file) void upload(file);
});

setKeyStatus();
updateCurrentInfo();
void loadPhotos().catch((error: unknown) => {
  showToast(error instanceof Error ? error.message : "无法读取照片库。", "error");
});
